import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getHourLimits, upsertProjectWeeklyLimit, getManagementHours,
  bulkUpsertWeeklyLimits, setProjectTotalLimit,
  downloadHourLimitsTemplate, importHourLimits,
} from '../../api/client'
import { currentLocale } from '../../i18n'

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

// The two frozen columns are pinned by pixel offset, so their widths have to be
// fixed rather than min-widths — the second sticks at exactly the first's width.
const PROJECT_W = 260
const FILL_W = 132

const SECONDARY_BTN = 'bg-white border border-gray-200 text-gray-700 px-3.5 py-2 rounded-lg ' +
  'text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors'

function monthLabel(month) {
  return new Date(2000, month - 1, 1).toLocaleDateString(currentLocale(), { month: 'short' })
}

// "" -> null so a cleared cell removes the limit rather than saving 0.
function parseCell(raw) {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) && n >= 0 ? n : undefined  // undefined = invalid, reject
}

export default function WeeklyHourLimits() {
  const { t } = useTranslation('engineering')
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const yearRef = useRef(year)
  yearRef.current = year   // always the freshest year, readable from async callbacks below
  const [data, setData] = useState(null)
  const [actuals, setActuals] = useState({})   // `${project_id}-${month}` -> { hours, ceiling }
  const [saving, setSaving] = useState(null)   // `${year}-${project_id}-${month}-${field}`
  const [savedCell, setSavedCell] = useState(null)   // `${year}-${project_id}-${month}` — brief post-save tick
  const [error, setError] = useState(null)
  const [overwrite, setOverwrite] = useState(false)   // fill replaces months that already have a value
  const [filling, setFilling] = useState(null)        // project_id whose row fill is in flight
  const [transfer, setTransfer] = useState(null)      // 'download' | 'upload' while in flight
  const [importResult, setImportResult] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)       // bumped to re-pull the grid after an upload
  const fileInputRef = useRef(null)
  const savedTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(savedTimerRef.current), [])

  useEffect(() => {
    let cancelled = false
    setData(null)
    getHourLimits(year)
      .then(d => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setData({ projects: [], limits: [] }) })
    return () => { cancelled = true }
  }, [year, reloadKey])

  // Actual hours for the current year, so cells already over their max show red.
  useEffect(() => {
    let cancelled = false
    setActuals({})
    if (year !== currentYear) return
    getManagementHours('ytd', false)
      .then(r => {
        if (cancelled) return
        const map = {}
        for (const p of r.projects) {
          for (const m of p.months) {
            map[`${p.project_id}-${m.month}`] = { hours: m.hours, ceiling: m.ceiling }
          }
        }
        setActuals(map)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [year, currentYear])

  const limitMap = useMemo(() => {
    const map = {}
    for (const b of data?.limits || []) {
      map[`${b.project_id}-${b.month}`] = b
    }
    return map
  }, [data])

  const save = async (projectId, month, field, raw, previous) => {
    const value = parseCell(raw)
    if (value === undefined) {
      setError(t('management.limits.saveFailed'))
      return previous
    }
    // Snapshot the year this save belongs to. `year` is a normal closed-over
    // value here and never changes for the life of this call, so it's a
    // stable stand-in for "the year this edit was made against" — compare it
    // against `yearRef.current` (always fresh) once the request settles to
    // detect a year switch that happened while the request was in flight.
    const saveYear = year
    const existing = limitMap[`${projectId}-${month}`] || {}
    const payload = {
      project_id: projectId,
      year: saveYear,
      month,
      min_weekly_hours: field === 'min' ? value : (existing.min_weekly_hours ?? null),
      max_weekly_hours: field === 'max' ? value : (existing.max_weekly_hours ?? null),
    }
    const key = `${saveYear}-${projectId}-${month}-${field}`
    const savedKey = `${saveYear}-${projectId}-${month}`
    setSaving(key)
    setError(null)
    try {
      const saved = await upsertProjectWeeklyLimit(payload)
      if (yearRef.current === saveYear) {
        setData(d => (d ? {
          ...d,
          limits: [
            ...d.limits.filter(b => !(b.project_id === projectId && b.month === month)),
            { project_id: projectId, month, min_weekly_hours: saved.min_weekly_hours, max_weekly_hours: saved.max_weekly_hours },
          ],
        } : d))
        setSavedCell(savedKey)
        clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => {
          setSavedCell(c => (c === savedKey ? null : c))
        }, 1500)
      }
      return value
    } catch {
      if (yearRef.current === saveYear) setError(t('management.limits.saveFailed'))
      return previous
    } finally {
      setSaving(s => (s === key ? null : s))
    }
  }

  // Fill one min/max pair across all 12 months of a project in a single call.
  const fillRow = async (projectId, rawMin, rawMax) => {
    const min = parseCell(rawMin)
    const max = parseCell(rawMax)
    if (min === undefined || max === undefined) {
      setError(t('management.limits.saveFailed'))
      return false
    }
    if (min == null && max == null) {
      setError(t('management.limits.fillNeedsValue'))
      return false
    }
    if (min != null && max != null && min > max) {
      setError(t('management.limits.fillInverted'))
      return false
    }
    // Same year-switch guard as save(): the request outlives the click, and the
    // grid may already be showing a different year by the time it lands.
    const fillYear = year
    setFilling(projectId)
    setError(null)
    try {
      const filled = await bulkUpsertWeeklyLimits({
        projectId, year: fillYear, minWeeklyHours: min, maxWeeklyHours: max,
        onlyEmpty: !overwrite,
      })
      if (yearRef.current === fillYear) {
        setData(d => (d ? {
          ...d,
          limits: [
            ...d.limits.filter(b => b.project_id !== projectId),
            ...filled.map(b => ({
              project_id: projectId, month: b.month,
              min_weekly_hours: b.min_weekly_hours, max_weekly_hours: b.max_weekly_hours,
            })),
          ],
        } : d))
      }
      return true
    } catch {
      if (yearRef.current === fillYear) setError(t('management.limits.fillFailed'))
      return false
    } finally {
      setFilling(f => (f === projectId ? null : f))
    }
  }

  // The lifetime ceiling hangs off the project, not the year, so no year guard.
  const saveTotalLimit = async (projectId, raw, previous) => {
    const value = parseCell(raw)
    if (value === undefined) {
      setError(t('management.limits.saveFailed'))
      return previous
    }
    setError(null)
    try {
      await setProjectTotalLimit(projectId, value)
      setData(d => (d ? {
        ...d,
        projects: d.projects.map(p => (
          p.project_id === projectId ? { ...p, total_hour_limit: value } : p
        )),
      } : d))
      return value
    } catch {
      setError(t('management.limits.saveFailed'))
      return previous
    }
  }

  const downloadTemplate = async () => {
    setTransfer('download')
    setError(null)
    setImportResult(null)
    try {
      await downloadHourLimitsTemplate(year)
    } catch {
      setError(t('management.limits.templateFailed'))
    } finally {
      setTransfer(null)
    }
  }

  // The file is parsed against the year that was selected when it was picked —
  // same year-switch guard as save(), since the upload outlives the click.
  const uploadTemplate = async (file) => {
    if (!file) return
    const uploadYear = year
    setTransfer('upload')
    setError(null)
    setImportResult(null)
    try {
      const result = await importHourLimits(uploadYear, file)
      if (yearRef.current === uploadYear) {
        setImportResult(result)
        setReloadKey(k => k + 1)
      }
    } catch {
      if (yearRef.current === uploadYear) setError(t('management.limits.templateFailed'))
    } finally {
      setTransfer(null)
    }
  }

  return (
    <div className="px-8 pb-8 pt-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight">
            {t('management.limits.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('management.limits.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadTemplate}
              disabled={transfer !== null || !data}
              title={t('management.limits.templateDownloadTooltip')}
              className={SECONDARY_BTN}
            >
              {transfer === 'download'
                ? t('management.limits.templateWorking')
                : t('management.limits.templateDownload')}
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={transfer !== null || !data}
              title={t('management.limits.templateDownloadTooltip')}
              className={SECONDARY_BTN}
            >
              {transfer === 'upload'
                ? t('management.limits.templateWorking')
                : t('management.limits.templateUpload')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0]
                e.target.value = ''   // so picking the same file twice still fires
                uploadTemplate(file)
              }}
            />
          </div>
          <label
            title={t('management.limits.fillOverwriteTooltip')}
            className="flex items-center gap-2 text-sm text-gray-600"
          >
            <input
              type="checkbox"
              checked={overwrite}
              onChange={e => setOverwrite(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('management.limits.fillOverwrite')}
          </label>
          <select
            aria-label={t('management.limits.year')}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      {importResult && <ImportSummary result={importResult} t={t} />}

      {!data ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t('management.loading')}</p>
      ) : data.projects.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t('management.limits.empty')}</p>
      ) : (
        // Capped height so the header row has something to stick against —
        // `sticky top-0` resolves against this scroll container, not the page.
        <div className="bg-white rounded-xl border border-gray-200 overflow-auto max-h-[70vh]">
          <table className="text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  style={{ width: PROJECT_W, minWidth: PROJECT_W, maxWidth: PROJECT_W }}
                  className="sticky left-0 top-0 z-30 bg-white px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-r border-gray-200"
                >
                  {t('management.limits.project')}
                </th>
                <th
                  title={t('management.limits.fillTooltip')}
                  style={{ left: PROJECT_W, width: FILL_W, minWidth: FILL_W, maxWidth: FILL_W }}
                  className="sticky top-0 z-30 bg-white px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-r border-gray-200"
                >
                  {t('management.limits.fill')}
                </th>
                {MONTHS.map(m => (
                  <th key={m} className="sticky top-0 z-20 bg-white px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase border-b border-gray-200 min-w-[120px]">
                    {monthLabel(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.projects.map(p => (
                <tr key={p.project_id}>
                  <td
                    style={{ width: PROJECT_W, minWidth: PROJECT_W, maxWidth: PROJECT_W }}
                    className="sticky left-0 z-10 bg-white px-4 py-2 border-b border-r border-gray-100"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: p.color }} />
                      <span className="font-medium text-gray-800 truncate">{p.name}</span>
                      {p.code && <span className="text-xs text-gray-400 shrink-0">{p.code}</span>}
                    </div>
                    <TotalLimitCell project={p} t={t} onCommit={saveTotalLimit} />
                  </td>
                  <td
                    style={{ left: PROJECT_W, width: FILL_W, minWidth: FILL_W, maxWidth: FILL_W }}
                    className="sticky z-10 bg-white px-2 py-2 border-b border-r border-gray-100"
                  >
                    <FillRowCell
                      t={t}
                      busy={filling === p.project_id}
                      onApply={(min, max) => fillRow(p.project_id, min, max)}
                    />
                  </td>
                  {MONTHS.map(m => {
                    const band = limitMap[`${p.project_id}-${m}`]
                    const actual = actuals[`${p.project_id}-${m}`]
                    // The stored figure is per week; what the month's hours can
                    // be judged against is the ceiling the backend derives from
                    // it (rate x weeks in the month).
                    const over = actual?.ceiling != null && actual.hours > actual.ceiling
                    const cellKey = `${year}-${p.project_id}-${m}`
                    // Both inputs in a cell lock together while either field
                    // is saving (rather than each locking independently).
                    // The POST overwrites min AND max together server-side,
                    // so `save()` always sends both — if the max input stayed
                    // enabled while the min save was in flight, committing it
                    // right then would read the pre-edit min out of `limitMap`
                    // (which hasn't heard back yet) and silently write that
                    // stale value over the min edit still in flight. Locking
                    // the whole cell means a sibling save can only ever
                    // dispatch after the previous one has fully resolved and
                    // `limitMap` reflects it.
                    const cellBusy = saving === `${cellKey}-min` || saving === `${cellKey}-max`
                    const cellSaved = savedCell === cellKey
                    return (
                      <td key={m}
                          title={over ? t('management.limits.overTooltip', { hours: actual.hours }) : undefined}
                          className={`px-2 py-2 border-b border-gray-100 ${over ? 'bg-red-50' : ''}`}>
                        <div className="flex items-center gap-1">
                          <LimitInput
                            defaultValue={band?.min_weekly_hours ?? ''}
                            placeholder={t('management.limits.min')}
                            busy={cellBusy}
                            onCommit={(raw, prev) => save(p.project_id, m, 'min', raw, prev)}
                          />
                          <LimitInput
                            defaultValue={band?.max_weekly_hours ?? ''}
                            placeholder={t('management.limits.max')}
                            busy={cellBusy}
                            onCommit={(raw, prev) => save(p.project_id, m, 'max', raw, prev)}
                          />
                          {cellSaved && (
                            <span className="text-[10px] text-emerald-600 font-medium whitespace-nowrap">
                              {t('management.limits.saved')}
                            </span>
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function fmtHours(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

// What the upload actually did. Rows the server could not use are listed rather
// than summed, since the fix is always "go look at that row in the file".
function ImportSummary({ result, t }) {
  const changed = result.cells_updated + result.total_limits_updated
  const skipped = result.skipped || []
  return (
    <div className="mb-4 text-sm">
      <p className={changed ? 'text-emerald-700' : 'text-gray-500'}>
        {changed
          ? t('management.limits.templateResult', {
              cells: changed, projects: result.projects_updated,
            })
          : t('management.limits.templateNoChanges')}
      </p>
      {skipped.length > 0 && (
        <details className="mt-1.5">
          <summary className="text-amber-700 cursor-pointer">
            {t('management.limits.templateSkippedTitle', { count: skipped.length })}
          </summary>
          <ul className="mt-1 ml-4 list-disc text-xs text-gray-600 space-y-0.5">
            {skipped.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </details>
      )}
    </div>
  )
}

// The whole-life ceiling, shown against every hour ever logged — so it reads
// the same no matter which year the grid is on.
function TotalLimitCell({ project, t, onCommit }) {
  const used = project.lifetime_hours || 0
  const limit = project.total_hour_limit
  const over = limit != null && used > limit
  return (
    <div className="flex items-center gap-1.5 mt-1 pl-5">
      <LimitInput
        defaultValue={limit ?? ''}
        placeholder={t('management.limits.totalLimit')}
        title={t('management.limits.totalLimitTooltip')}
        width="w-16"
        invalid={over}
        onCommit={(raw, prev) => onCommit(project.project_id, raw, prev)}
      />
      <span className={`text-[10px] truncate ${over ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
        {over
          ? t('management.limits.totalOver', {
              hours: fmtHours(used), over: fmtHours(used - limit),
            })
          : t('management.limits.totalUsed', { hours: fmtHours(used) })}
      </span>
    </div>
  )
}

function FillRowCell({ t, busy, onApply }) {
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')

  const apply = async () => {
    const ok = await onApply(min, max)
    if (ok) { setMin(''); setMax('') }
  }

  const cls = 'w-12 px-1.5 py-1 text-xs text-center tabular-nums border border-gray-200 rounded focus:border-gray-400 focus:outline-none disabled:opacity-50'
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1">
        <input type="text" inputMode="decimal" value={min} disabled={busy}
               placeholder={t('management.limits.min')} className={cls}
               onChange={e => setMin(e.target.value)} />
        <input type="text" inputMode="decimal" value={max} disabled={busy}
               placeholder={t('management.limits.max')} className={cls}
               onChange={e => setMax(e.target.value)} />
      </div>
      <button
        onClick={apply}
        disabled={busy || (min.trim() === '' && max.trim() === '')}
        className="w-full text-[10px] font-medium px-2 py-1 rounded bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-gray-900 transition-colors"
      >
        {busy ? t('management.limits.fillApplying') : t('management.limits.fillApply')}
      </button>
    </div>
  )
}

function LimitInput({ defaultValue, placeholder, busy, onCommit, title, width = 'w-12', invalid = false }) {
  const [value, setValue] = useState(String(defaultValue ?? ''))

  useEffect(() => { setValue(String(defaultValue ?? '')) }, [defaultValue])

  const commit = async () => {
    const previous = String(defaultValue ?? '')
    if (value === previous) return
    const result = await onCommit(value, previous)
    setValue(result == null ? '' : String(result))
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      placeholder={placeholder}
      title={title}
      disabled={busy}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
      className={`${width} px-1.5 py-1 text-xs text-center tabular-nums border rounded focus:outline-none disabled:opacity-50 ${
        invalid
          ? 'border-red-300 bg-red-50 text-red-700 focus:border-red-400'
          : 'border-gray-200 focus:border-gray-400'
      }`}
    />
  )
}
